/**
 * Bioteiga Teichwerte-Tracker
 */

/**
 * UI translations.
 *
 * Every user-facing string lives in the theme locale files under `bioteiga.teichwerte.*`
 * and is injected by snippets/bioteiga-teichwerte-i18n.liquid. That makes each one
 * translatable in Shopify's "Translate & Adapt" app (Theme content).
 *
 * Do NOT hardcode user-facing text here — add a key to locales/de.json + locales/en.default.json,
 * list it in the snippet, then reference it via bioteigaT('key', 'German fallback').
 * The fallback keeps the tracker working if the snippet is ever missing.
 *
 * Placeholders use %{name} and are replaced from the optional `vars` object.
 */
function bioteigaT(key, fallback, vars) {
  var dict = (window.BIOTEIGA_I18N && window.BIOTEIGA_I18N.teichwerte) || {};
  var out = dict[key];
  if (out === undefined || out === null || out === '') out = fallback;
  if (vars) {
    Object.keys(vars).forEach(function(name) {
      out = out.split('%{' + name + '}').join(vars[name]);
    });
  }
  return out;
}

// Product recommendations per water value problem
const WATER_PROBLEMS = {
  ph: {
    tag: 'kr-ph-wert',
    high: { title: bioteigaT('problem_ph_high_title', 'pH-Wert zu hoch'), desc: bioteigaT('problem_ph_desc', 'Bei einem abweichenden pH-Wert (zu sauer oder zu basisch) finden keine wasserreinigenden Prozesse im Teich statt, Wasserpflanzen können nicht wachsen und Wasserlebewesen erkranken.') },
    low:  { title: bioteigaT('problem_ph_low_title', 'pH-Wert zu niedrig'), desc: bioteigaT('problem_ph_desc', 'Bei einem abweichenden pH-Wert (zu sauer oder zu basisch) finden keine wasserreinigenden Prozesse im Teich statt, Wasserpflanzen können nicht wachsen und Wasserlebewesen erkranken.') },
  },
  kh: {
    tag: 'kr-kh-karbonathaerte',
    low:  { title: bioteigaT('problem_kh_low_title', 'Karbonathärte zu niedrig'), desc: bioteigaT('problem_kh_low_desc', 'Die Karbonathärte regelt den pH-Wert des Teichwassers. Ist die Karbonathärte zu niedrig, befindet sich der pH-Wert nicht mehr im optimalen Bereich oder ist ständigen Schwankungen ausgesetzt. Biologische Prozesse können nicht mehr stattfinden.') },
  },
  no3: {
    tag: 'kr-no3-nitrat',
    high: { title: bioteigaT('problem_no3_high_title', 'Nitrat zu hoch'), desc: bioteigaT('problem_no3_high_desc', 'Ein zu hoher Nitrat-Wert begünstigt stark das Algenwachstum, führt zu grünem Wasser und macht Fische krank.') },
  },
  po4: {
    tag: 'kr-po4-phosphat',
    high: { title: bioteigaT('problem_po4_high_title', 'Phosphat zu hoch'), desc: bioteigaT('problem_po4_high_desc', 'Phosphat wird nur von Pflanzen im Teich als Nährstoff aufgenommen – ein Überschuss dient jedoch den Algen als Hauptnahrung. Ab einem Wert von 0,1 mg/l kann die intensive Algenbildung einsetzen.') },
  },
  gh: {
    tag: 'kr-gh-gesamthaerte',
    low:  { title: bioteigaT('problem_gh_low_title', 'Gesamthärte zu niedrig'), desc: bioteigaT('problem_gh_low_desc', 'Die Gesamthärte ist ein Maß für die Gesamtsumme der im Wasser gelösten Mineralien. Diese Mineralien sind für eine optimal funktionierende Teichbiologie und ein gesundes Wachstum von Pflanzen und Fischen unerlässlich.') },
  },
  temperature: {
    tag: 'kr-temperatur',
    high: { title: bioteigaT('problem_temperature_high_title', 'Wassertemperatur zu hoch'), desc: bioteigaT('problem_temperature_high_desc', 'Alle biologischen Prozesse laufen auf Hochtouren, jedoch nimmt der Sauerstoffgehalt und die Karbonathärte stark ab. Mit weiter steigenden Wassertemperaturen empfehlen wir eine Beschattung des Teiches, Frischwasser zu geben und die Wasserwerte regelmäßiger im Auge behalten.') },
    low:  { title: bioteigaT('problem_temperature_low_title', 'Wassertemperatur zu niedrig'), desc: bioteigaT('problem_temperature_low_desc', 'Bei niedrigen Wassertemperaturen finden alle biologischen Prozesse (auch der Algenabbau) nur sehr eingeschränkt statt. Die Fischfütterung sollte ebenfalls eingestellt sein, da der Stoffwechsel der Fische nur begrenzt arbeitet.') },
  },
  cl: {
    tag: 'kr-cl-chlor',
    high: { title: bioteigaT('problem_cl_high_title', 'Chlorbelastung erhöht'), desc: bioteigaT('problem_cl_high_desc', 'Chlor ist ein sehr starkes Desinfektionsmittel und zerstört die gesamte Teichbiologie. Für alle Teichlebewesen und wasserreinigenden Mikroorganismen ist ein Chlor-Wert über 0,0 mg/l giftig und führt unweigerlich zum Absterben.') },
  },
  cu: {
    tag: 'kr-cu-kupfer',
    high: { title: bioteigaT('problem_cu_high_title', 'Kupfergehalt erhöht'), desc: bioteigaT('problem_cu_high_desc', 'Kupfer ist für ein breites Spektrum von Mikroorganismen sowie für wirbellose Tiere giftig. Aber auch Fische reagieren sehr empfindlich auf Kupferwerte über 0,0 mg/l.') },
  },
  o2: {
    tag: 'kr-o2-sauerstoff',
    low:  { title: bioteigaT('problem_o2_low_title', 'Sauerstoffgehalt zu niedrig'), desc: bioteigaT('problem_o2_low_desc', 'Ein Sauerstoffgehalt von mindestens 6 mg/l ist für alle Lebewesen und biologischen Prozesse im Teich notwendig.') },
  },
  no2: {
    tag: 'kr-no2-nitrit',
    high: { title: bioteigaT('problem_no2_high_title', 'Nitrit erhöht – kritisch!'), desc: bioteigaT('problem_no2_high_desc', 'Nitrit ist ein ernstzunehmendes Fischgift. Eine mangelnde biologische Filterung ist dafür verantwortlich.') },
  },
  nh4: {
    tag: 'kr-nh4-nh3-ammonium',
    high: { title: bioteigaT('problem_nh4_high_title', 'Ammonium erhöht'), desc: bioteigaT('problem_nh4_high_desc', 'Zu viel Futter, Fischausscheidungen und abgestorbene Pflanzenteile belasten das Teichwasser mit Ammonium. Bei einem pH-Wert über 9 wandelt sich das harmlose Ammonium in hochgiftiges Ammoniak um. Ein Ammoniak-Wert über 0,5 mg/l führt bei Fischen zu schwerwiegenden Erkrankungen.') },
  },
  fe: {
    tag: 'kr-fe-eisen',
    high: { title: bioteigaT('problem_fe_high_title', 'Eisengehalt erhöht'), desc: bioteigaT('problem_fe_high_desc', 'Ein geringer Eisengehalt ist nachteilig für Pflanzen, zu viel Eisen schädigt jedoch die Fische. Um optimales Pflanzenwachstum zu ermöglichen und gleichzeitig den Wasserlebewesen nicht zu schaden, sollte ein maximaler Eisengehalt von 0,25 mg/l im Teich nicht überschritten werden.') },
  },
};

// Weights for health score (higher = more critical)
const HEALTH_WEIGHTS = {
  no2: 3, nh4: 3, cl: 3, cu: 3,   // akut gefährlich
  ph:  2, kh:  2, o2: 2,           // sehr wichtig
  no3: 1, po4: 1, gh: 1, temperature: 1, fe: 1,
};

// Critical values that should always be measured — show warning if missing
const CRITICAL_UNMEASURED = ['ph', 'kh', 'no3', 'po4', 'o2', 'temperature'];

const WATER_VALUES = {
  ph:          { label: bioteigaT('wv_ph', 'pH-Wert'),       symbol: 'pH',  unit: '',      color: '#E91E63', min: 7.5,   max: 8.5,   step: 0.1,   group: 'basic' },
  kh:          { label: bioteigaT('wv_kh', 'Karbonathärte'), symbol: 'kH',  unit: '°dkH', color: '#F47B20', min: 9,     max: null,  step: 0.5,   group: 'basic' },
  no3:         { label: bioteigaT('wv_no3', 'Nitrat'),        symbol: 'NO₃', unit: 'mg/l', color: '#8A1E48', min: 0,     max: 10,    step: 0.5,   group: 'basic' },
  po4:         { label: bioteigaT('wv_po4', 'Phosphat'),      symbol: 'PO₄', unit: 'mg/l', color: '#54244D', min: 0,     max: 0.05,  step: 0.01,  group: 'basic' },
  o2:          { label: bioteigaT('wv_o2', 'Sauerstoff'),    symbol: 'O₂',  unit: 'mg/l', color: '#26C6DA', min: 6,     max: null,  step: 0.5,   group: 'basic' },
  temperature: { label: bioteigaT('wv_temperature', 'Temperatur'),    symbol: '°C',  unit: '°C',   color: '#78909C', min: 10,    max: 25,    step: 0.5,   group: 'basic' },
  gh:          { label: bioteigaT('wv_gh', 'Gesamthärte'),   symbol: 'gH',  unit: '°dgH', color: '#1A3A7A', min: 10,    max: null,  step: 0.5,   group: 'extended' },
  cl:          { label: bioteigaT('wv_cl', 'Chlor'),         symbol: 'Cl',  unit: 'mg/l', color: '#42A5F5', min: 0,     max: 0.05,  step: 0.01,  group: 'extended' },
  cu:          { label: bioteigaT('wv_cu', 'Kupfer'),        symbol: 'Cu',  unit: 'mg/l', color: '#8D6E63', min: 0,     max: 0.05,  step: 0.001, group: 'extended' },
  no2:         { label: bioteigaT('wv_no2', 'Nitrit'),        symbol: 'NO₂', unit: 'mg/l', color: '#00897B', min: 0,     max: 0.05,  step: 0.001, group: 'extended' },
  nh4:         { label: bioteigaT('wv_nh4', 'Ammonium'),      symbol: 'NH₄', unit: 'mg/l', color: '#F9A825', min: 0,     max: 0.009, step: 0.001, group: 'extended' },
  fe:          { label: bioteigaT('wv_fe', 'Eisen'),         symbol: 'Fe',  unit: 'mg/l', color: '#7B1FA2', min: 0,     max: 0.25,  step: 0.01,  group: 'extended' },
};

class TeichwerteTracker extends HTMLElement {
  constructor() {
    super();
    this.readings = [];
    this.view = 'dashboard';
    this.editingId = null;
  }

  connectedCallback() {
    this.proxyBase = this.dataset.proxyBase || '/apps/teichwerte/api';
    this.customerId = this.dataset.customerId;
    this.teichgroesse = parseInt(this.dataset.teichgroesse, 10) || 0;
    this.fische = this.dataset.fische === 'true';
    this.teichpflanzen = this.dataset.teichpflanzen || '';
    this._render();
    this._loadReadings();
  }

  _render() {
    this.innerHTML = `
      <div class="teichwerte">
        <div class="teichwerte__message" data-message></div>
        <div data-view="dashboard">
          ${this._renderDashboard()}
        </div>
        <div data-view="form" class="teichwerte--hidden">
          ${this._renderForm()}
        </div>
      </div>
    `;
    this._bindEvents();
  }

  _renderDashboard() {
    return `
      <div class="teichwerte__dashboard">
        <div class="teichwerte__dashboard-header">
          <h2 class="teichwerte__dashboard-title">${bioteigaT('page_title', 'Meine Teichwerte')}</h2>
          <button class="teichwerte__btn teichwerte__btn--primary" data-action="show-form">
            ${bioteigaT('add_values', '+ Werte hinzufügen')}
          </button>
        </div>
        ${this._renderPondOverview()}
        <div data-cards-container>
          <div class="teichwerte__loading">
            <div class="teichwerte__spinner"></div>
            <p>${bioteigaT('loading', 'Lade Daten...')}</p>
          </div>
        </div>
        <div data-recs-container></div>
        <div data-history-container></div>
      </div>
    `;
  }

  _renderPondOverview() {
    const hasData = this.teichgroesse > 0 || this.fische || (this.teichpflanzen && this.teichpflanzen !== 'keine');

    if (!hasData) {
      return `
        <div class="teichwerte__pond-overview">
          <div class="teichwerte__health-card" data-health-container>
            <div class="teichwerte__health-loading">
              <div class="teichwerte__spinner"></div>
            </div>
          </div>
          <div class="teichwerte__pond-info-card teichwerte__pond-empty">
            <div class="teichwerte__pond-empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="32" height="32">
                <path d="M12 2C6 8 4 12 4 15a8 8 0 0016 0c0-3-2-7-8-13z"/>
              </svg>
            </div>
            <h3 class="teichwerte__pond-title">${bioteigaT('pond_title', 'Mein Teich')}</h3>
            <p class="teichwerte__pond-empty-text">${bioteigaT('pond_empty_text', 'Beschreiben Sie Ihren Teich, damit wir Ihnen bessere Empfehlungen geben können.')}</p>
            <button class="teichwerte__btn teichwerte__btn--primary" data-action="edit-pond">${bioteigaT('pond_setup_cta', 'Teich einrichten')}</button>
          </div>
        </div>
      `;
    }

    const sizeFormatted = this.teichgroesse > 0
      ? this.teichgroesse.toLocaleString('de-DE') + ' Liter'
      : null;

    const plantsLabel = {
      keine: bioteigaT('plants_none', 'Keine Pflanzen'),
      wenig: bioteigaT('plants_few', 'Wenig bepflanzt'),
      viele: bioteigaT('plants_many', 'Stark bepflanzt'),
    }[this.teichpflanzen] || null;

    const items = [];

    if (sizeFormatted) {
      items.push(`
        <div class="teichwerte__pond-item">
          <div class="teichwerte__pond-item-icon">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <ellipse cx="10" cy="13" rx="7" ry="4"/>
              <path d="M3 13V9a7 4 0 0 1 14 0v4"/>
              <path d="M7 9.5c1-1.5 5-1.5 6 0"/>
            </svg>
          </div>
          <div class="teichwerte__pond-item-body">
            <div class="teichwerte__pond-item-label">${bioteigaT('pond_size', 'Teichgröße')}</div>
            <div class="teichwerte__pond-item-value">${sizeFormatted}</div>
          </div>
        </div>
      `);
    }

    items.push(`
      <div class="teichwerte__pond-item">
        <div class="teichwerte__pond-item-icon ${this.fische ? 'is-active' : ''}">
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 10c2-4 10-5 14-1M3 10c2 4 10 5 14 1"/>
            <path d="M17 9c1-1.5 2-2 3-2s-1 1.5-1 3 2 3 1 3-2-.5-3-2Z"/>
            <circle cx="6" cy="9.5" r="0.75" fill="currentColor" stroke="none"/>
          </svg>
        </div>
        <div class="teichwerte__pond-item-body">
          <div class="teichwerte__pond-item-label">${bioteigaT('pond_fish', 'Fischbesatz')}</div>
          <div class="teichwerte__pond-item-value">${this.fische ? bioteigaT('fish_with', 'Mit Fischen') : bioteigaT('fish_without', 'Ohne Fische')}</div>
        </div>
      </div>
    `);

    if (plantsLabel) {
      items.push(`
        <div class="teichwerte__pond-item">
          <div class="teichwerte__pond-item-icon ${this.teichpflanzen !== 'keine' ? 'is-active' : ''}">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 17V9"/>
              <path d="M10 13c-1-3-5-4-7-2 2 0 4 1 5 3"/>
              <path d="M10 10c1-3 5-4 7-2-2 0-4 1-5 3"/>
              <path d="M6 17h8"/>
            </svg>
          </div>
          <div class="teichwerte__pond-item-body">
            <div class="teichwerte__pond-item-label">${bioteigaT('pond_plants', 'Bepflanzung')}</div>
            <div class="teichwerte__pond-item-value">${plantsLabel}</div>
          </div>
        </div>
      `);
    }

    const editBtn = `
      <button class="teichwerte__pond-edit-btn" data-action="edit-pond" title="${bioteigaT('action_edit', 'Bearbeiten')}">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="16" height="16">
          <path d="M11.5 2.5a1.414 1.414 0 0 1 2 2L5 13H3v-2L11.5 2.5Z"/>
        </svg>
      </button>
    `;

    return `
      <div class="teichwerte__pond-overview">
        <div class="teichwerte__health-card" data-health-container>
          <div class="teichwerte__health-loading">
            <div class="teichwerte__spinner"></div>
          </div>
        </div>
        <div class="teichwerte__pond-info-card">
          <div class="teichwerte__pond-header">
            <h3 class="teichwerte__pond-title">${bioteigaT('pond_title', 'Mein Teich')}</h3>
            ${editBtn}
          </div>
          <div class="teichwerte__pond-items">
            ${items.join('')}
          </div>
        </div>
      </div>
    `;
  }

  _renderHealthGauge() {
    const container = this.querySelector('[data-health-container]');
    if (!container) return;

    const result = this._calcHealthScore(0);
    if (result === null) {
      container.style.display = 'none';
      return;
    }
    const prevResult = this._calcHealthScore(1);
    const delta = prevResult !== null ? result.score - prevResult.score : null;

    // Raw score (only measured values, no penalty)
    const rawScore = this._calcRawHealthScore(0);

    container.style.display = '';
    container.innerHTML = `
      <h3 class="teichwerte__pond-title">${bioteigaT('health_title', 'Teichgesundheit')}</h3>
      ${this._buildHealthGauge(result.score, delta, rawScore, result.missingCritical, result.totalCritical)}
    `;
  }

  _scoreValue(key, value) {
    const def = WATER_VALUES[key];
    const status = this._getValueStatus(key, value);
    if (status === 'good') return 100;

    const rangeFactor = def.max !== null
      ? (def.max - def.min) * 0.2 || 0.5
      : def.min * 0.2 || 0.5;

    let deviation = 0;
    if (value < def.min) {
      deviation = (def.min - value) / rangeFactor;
    } else if (def.max !== null && value > def.max) {
      deviation = (value - def.max) / rangeFactor;
    }

    if (status === 'warning') {
      return Math.round(100 - deviation * 40);       // 60–99
    } else {
      return Math.max(0, Math.round(60 - (deviation - 1) * 30)); // 0–59
    }
  }

  _calcHealthScore(offset = 0) {
    if (this.readings.length <= offset) return null;
    const reading = this.readings[offset];
    let totalWeight = 0;
    let weightedScore = 0;
    let missingCriticalCount = 0;
    const totalCritical = CRITICAL_UNMEASURED.length;

    for (const key of Object.keys(WATER_VALUES)) {
      const weight = HEALTH_WEIGHTS[key] || 1;
      const value = reading[key];

      if (value == null) {
        // Missing value in this reading: penalize critical ones with score 50
        if (CRITICAL_UNMEASURED.includes(key)) {
          weightedScore += 50 * weight;
          totalWeight += weight;
          missingCriticalCount++;
        }
        continue;
      }
      const score = this._scoreValue(key, value);
      weightedScore += score * weight;
      totalWeight += weight;
    }

    if (totalWeight === 0) return null;

    const adjustedScore = Math.round(weightedScore / totalWeight);
    return { score: adjustedScore, missingCritical: missingCriticalCount, totalCritical };
  }

  _calcRawHealthScore(offset = 0) {
    if (this.readings.length <= offset) return null;
    const reading = this.readings[offset];
    let totalWeight = 0;
    let weightedScore = 0;
    for (const key of Object.keys(WATER_VALUES)) {
      const value = reading[key];
      if (value == null) continue;
      const score = this._scoreValue(key, value);
      const weight = HEALTH_WEIGHTS[key] || 1;
      weightedScore += score * weight;
      totalWeight += weight;
    }
    return totalWeight === 0 ? null : Math.round(weightedScore / totalWeight);
  }

  _buildHealthGauge(score, delta = null, rawScore = null, missingCritical = 0, totalCritical = 6) {
    const cx = 110, cy = 108, r = 82;
    const arcLen = Math.PI * r;

    const color = score >= 95 ? '#22C55E'
                : score >= 70 ? '#F59E0B'
                :               '#EF4444';

    const label = score >= 95 ? 'Ausgezeichnet'
                : score >= 70 ? bioteigaT('status_improve', 'Verbesserungswürdig')
                :               bioteigaT('status_critical', 'Kritisch');

    const dashOffset = (arcLen * (1 - score / 100)).toFixed(1);
    const x1 = cx - r, x2 = cx + r;

    // Potential arc (raw score without penalty) — shown as lighter color behind
    let potentialArc = '';
    if (missingCritical > 0 && rawScore !== null && rawScore > score) {
      const potentialColor = rawScore >= 95 ? '#22C55E'
                           : rawScore >= 70 ? '#F59E0B'
                           :                  '#EF4444';
      const potentialOffset = (arcLen * (1 - rawScore / 100)).toFixed(1);
      potentialArc = `
          <path d="M ${x1},${cy} A ${r},${r} 0 0 1 ${x2},${cy}"
                fill="none" stroke="${potentialColor}" stroke-width="14" stroke-linecap="round"
                opacity="0.2"
                stroke-dasharray="${arcLen.toFixed(1)} ${arcLen.toFixed(1)}"
                stroke-dashoffset="${potentialOffset}"/>`;
    }

    let deltaBadge = '';
    if (delta !== null) {
      const arrow  = delta > 0 ? '↑' : delta < 0 ? '↓' : '→';
      const sign   = delta > 0 ? '+' : '';
      const dColor = delta > 0 ? '#16A34A' : delta < 0 ? '#DC2626' : '#6B7280';
      const dBg    = delta > 0 ? '#F0FDF4' : delta < 0 ? '#FEF2F2' : '#F3F4F6';
      deltaBadge = `<span class="teichwerte__health-delta" style="color:${dColor};background:${dBg}">
        ${arrow} ${sign}${delta}
      </span>`;
    }

    // Completeness note under gauge
    let completenessNote = '';
    if (missingCritical > 0) {
      const measured = totalCritical - missingCritical;
      completenessNote = `<div class="teichwerte__health-completeness">${bioteigaT('health_completeness', '%{measured} von %{total} wichtige Werte gemessen', { measured: measured, total: totalCritical })}</div>`;
    }

    // Explanatory text based on score
    const recLink = `<a href="#teichwerte-empfehlungen" class="teichwerte__health-link">${bioteigaT('health_link', 'Zu den Empfehlungen →')}</a>`;
    let infoText = '';
    if (missingCritical > 0) {
      // Special text when values are missing
      if (rawScore !== null && rawScore >= 85) {
        infoText = bioteigaT('health_partial_good',
          '<p>Ihre gemessenen Werte sehen <strong>gut</strong> aus! Allerdings fehlen noch %{missing} wichtige Wasserwerte für eine vollständige Einschätzung.</p><p>Messen Sie alle wichtigen Werte, um Ihren tatsächlichen Gesundheitsscore zu sehen. %{link}</p>',
          { missing: missingCritical, link: recLink });
      } else {
        infoText = bioteigaT('health_partial_bad',
          '<p>Einige Ihrer Werte sind <strong>auffällig</strong> und es fehlen noch %{missing} wichtige Wasserwerte für eine vollständige Bewertung.</p><p>Messen Sie die fehlenden Werte und prüfen Sie die Empfehlungen. %{link}</p>',
          { missing: missingCritical, link: recLink });
      }
    } else if (score >= 95) {
      infoText = bioteigaT('health_excellent',
        '<p>Ihr Teich ist in einem <strong>ausgezeichneten Zustand</strong> – Sie leisten hervorragende Arbeit! Alle gemessenen Wasserwerte liegen im optimalen Bereich.</p><p>Testen Sie weiterhin regelmäßig Ihre Wasserwerte, um diesen Top-Zustand dauerhaft zu erhalten.</p>');
    } else if (score >= 70) {
      infoText = bioteigaT('health_good',
        '<p>Ihr Teich ist relativ gesund, aber einige Werte sind <strong>verbesserungswürdig</strong>. Mit gezielten Maßnahmen können Sie die Wasserqualität schnell verbessern.</p><p>Wir empfehlen Ihnen, die erkannten Probleme zu beheben. %{link}</p>',
        { link: recLink });
    } else {
      infoText = bioteigaT('health_critical',
        '<p>Einige Werte Ihres Teiches sind <strong>kritisch</strong> – sofortiges Handeln ist empfehlenswert, um Schäden an Fischen und Biologie zu vermeiden.</p><p>Bitte prüfen Sie umgehend die markierten Werte. %{link}</p>',
        { link: recLink });
    }

    const gaugeSvg = `
      <div class="teichwerte__gauge-wrap">
        ${deltaBadge}
        <svg viewBox="0 0 220 130" xmlns="http://www.w3.org/2000/svg"
             style="width:100%;height:auto;display:block;overflow:visible">
          <path d="M ${x1},${cy} A ${r},${r} 0 0 1 ${x2},${cy}"
                fill="none" stroke="#E5E7EB" stroke-width="14" stroke-linecap="round"/>
          ${potentialArc}
          <path d="M ${x1},${cy} A ${r},${r} 0 0 1 ${x2},${cy}"
                fill="none" stroke="${color}" stroke-width="14" stroke-linecap="round"
                stroke-dasharray="${arcLen.toFixed(1)} ${arcLen.toFixed(1)}"
                stroke-dashoffset="${dashOffset}"/>
          <text x="${x1}" y="${cy + 22}" text-anchor="middle"
                font-size="11" fill="rgba(0,0,0,0.35)" font-family="inherit">0</text>
          <text x="${x2}" y="${cy + 22}" text-anchor="middle"
                font-size="11" fill="rgba(0,0,0,0.35)" font-family="inherit">100</text>
          <text x="${cx}" y="${cy - 10}" text-anchor="middle"
                font-size="38" font-weight="800" fill="#111" font-family="inherit">${score}</text>
          <text x="${cx}" y="${cy + 18}" text-anchor="middle"
                font-size="13" font-weight="600" fill="${color}" font-family="inherit">${label}</text>
        </svg>
        ${completenessNote}
      </div>`;

    return `
      <div class="teichwerte__health-body">
        <div class="teichwerte__health-gauge-col">${gaugeSvg}</div>
        <div class="teichwerte__health-info-col">${infoText}</div>
      </div>
    `;
  }

  _renderForm() {
    const today = new Date().toISOString().split('T')[0];

    const buildCard = (key, def) => {
      const optLabel = def.max !== null
        ? `${def.min}–${def.max}${def.unit ? ' ' + def.unit : ''}`
        : `≥ ${def.min}${def.unit ? ' ' + def.unit : ''}`;
      return `
        <div class="teichwerte__form-card">
          <div class="teichwerte__form-card-header">
            <span class="teichwerte__value-badge" style="background:${def.color}">${def.symbol}</span>
            <span class="teichwerte__form-card-name">${def.label}</span>
          </div>
          <div class="teichwerte__form-card-input-wrap">
            <input type="number" class="teichwerte__value-input"
              name="${key}" step="${def.step}" min="0" placeholder="—" inputmode="decimal">
            ${def.unit ? `<span class="teichwerte__form-card-unit">${def.unit}</span>` : ''}
          </div>
          <div class="teichwerte__form-card-range" style="color:${def.color}">
            Optimum: ${optLabel}
          </div>
        </div>
      `;
    };

    let basicCards = '';
    let extendedCards = '';
    for (const [key, def] of Object.entries(WATER_VALUES)) {
      if (def.group === 'basic') basicCards += buildCard(key, def);
      else extendedCards += buildCard(key, def);
    }

    return `
      <div class="teichwerte__form-view">
        <div class="teichwerte__form-view-header">
          <button class="teichwerte__back-btn" data-action="show-dashboard">${bioteigaT('form_back', '← Zurück')}</button>
          <h3 class="teichwerte__form-view-title" data-form-title>${bioteigaT('form_title', 'Werte eintragen')}</h3>
        </div>

        <p class="teichwerte__form-intro">${bioteigaT('form_intro', 'Tragen Sie Ihre aktuellen Messwerte Ihres Teiches ein. Sie können Werte auch frei lassen, wenn Sie diese nicht messen konnten.')}</p>

        <div class="teichwerte__date-row">
          <label>${bioteigaT('form_date', 'Messdatum')}</label>
          <input type="date" name="measured_at" value="${today}" max="${today}">
        </div>

        <div class="teichwerte__form-section">
          <div class="teichwerte__form-section-label">${bioteigaT('form_main_values', 'Wichtigste Messungen')}</div>
          <p class="teichwerte__form-section-desc">${bioteigaT('form_main_desc', 'Diese sechs Wasserwerte sind entscheidend für die Gesundheit Ihres Teiches und sollten bei jeder Messung erfasst werden. Sie geben Aufschluss über die grundlegende Wasserqualität und das biologische Gleichgewicht.')}</p>
          <div class="teichwerte__form-cards-grid" data-form>${basicCards}</div>
        </div>

        <div class="teichwerte__form-section">
          <div class="teichwerte__form-section-label">
            ${bioteigaT('form_extended', 'Erweiterte Werte')}
            <span class="teichwerte__optional-tag">${bioteigaT('form_optional', 'optional')}</span>
          </div>
          <p class="teichwerte__form-section-desc">${bioteigaT('form_extended_desc', 'Diese Werte sind optional, sollten aber gelegentlich mitgemessen werden — besonders wenn Ihr Teich Auffälligkeiten zeigt wie grünes oder trübes Wasser, Algenwachstum, Fischkrankheiten oder unangenehme Gerüche.')}</p>
          <div class="teichwerte__form-cards-grid" data-form-extended>${extendedCards}</div>
        </div>

        <div class="teichwerte__form-section">
          <div class="teichwerte__form-section-label">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="14" height="14" style="margin-right:4px">
              <path d="M14 1H2a1 1 0 0 0-1 1v12l3-2h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1Z"/>
              <path d="M5 5h6M5 8h4"/>
            </svg>
            ${bioteigaT('form_note', 'Anmerkung')}
            <span class="teichwerte__optional-tag">${bioteigaT('form_optional', 'optional')}</span>
          </div>
          <p class="teichwerte__form-section-desc">${bioteigaT('form_note_hint', 'Halten Sie fest, was Ihnen am Teich aufgefallen ist — z.B. trübes Wasser, Algenbildung, auffälliges Verhalten der Fische oder durchgeführte Maßnahmen.')}</p>
          <textarea class="teichwerte__notes-input" name="notes" rows="3" placeholder="${bioteigaT('form_note_placeholder', 'z.B. Wasser leicht grünlich, Teilwasserwechsel durchgeführt...')}"></textarea>
        </div>

        <div class="teichwerte__form-actions">
          <button class="teichwerte__btn teichwerte__btn--outline" data-action="reset">${bioteigaT('form_reset', 'Zurücksetzen')}</button>
          <button class="teichwerte__btn teichwerte__btn--primary" data-action="save">${bioteigaT('form_save', 'Speichern')}</button>
        </div>
      </div>
    `;
  }

  // ---- Events ----

  _bindEvents() {
    this.querySelector('[data-action="show-form"]')?.addEventListener('click', () => this._showForm());
    this.querySelector('[data-action="show-dashboard"]')?.addEventListener('click', () => this._showDashboard());
    this.querySelector('[data-action="save"]')?.addEventListener('click', () => this._save());
    this.querySelector('[data-action="reset"]')?.addEventListener('click', () => this._resetForm());
    this.querySelector('[data-action="edit-pond"]')?.addEventListener('click', () => this._showPondEditModal());
  }

  // ---- Pond Edit Modal ----

  _showPondEditModal() {
    // Remove existing modal if present
    this.querySelector('.teichwerte__pond-modal-overlay')?.remove();

    const fischeJaActive = this.fische ? 'is-active' : '';
    const fischeNeinActive = !this.fische ? 'is-active' : '';

    const modalHtml = `
      <div class="teichwerte__pond-modal-overlay" data-pond-overlay>
        <div class="teichwerte__pond-modal">
          <div class="teichwerte__pond-modal-header">
            <h3>${bioteigaT('pond_edit_title', 'Mein Teich bearbeiten')}</h3>
            <button class="teichwerte__pond-modal-close" data-action="cancel-pond-edit">&times;</button>
          </div>

          <div class="teichwerte__pond-modal-body">
            <div class="teichwerte__pond-modal-field">
              <label>${bioteigaT('pond_size_label', 'Teichgröße (Liter)')}</label>
              <input type="number" min="0" max="1000000" step="1" data-pond-field="teichgroesse"
                     value="${this.teichgroesse || ''}" placeholder="${bioteigaT('pond_size_placeholder', 'z.B. 2667')}" inputmode="numeric">
            </div>

            <div class="teichwerte__pond-modal-field">
              <label>${bioteigaT('pond_fish', 'Fischbesatz')}</label>
              <div class="teichwerte__pond-toggle" data-pond-field="fische">
                <button type="button" class="teichwerte__pond-toggle-btn ${fischeJaActive}" data-value="true">${bioteigaT('pond_fish_yes', 'Ja')}</button>
                <button type="button" class="teichwerte__pond-toggle-btn ${fischeNeinActive}" data-value="false">${bioteigaT('pond_fish_no', 'Nein')}</button>
              </div>
            </div>

            <div class="teichwerte__pond-modal-field">
              <label>${bioteigaT('pond_plants', 'Bepflanzung')}</label>
              <select data-pond-field="teichpflanzen">
                <option value="keine" ${this.teichpflanzen === 'keine' || !this.teichpflanzen ? 'selected' : ''}>${bioteigaT('plants_none', 'Keine Pflanzen')}</option>
                <option value="wenig" ${this.teichpflanzen === 'wenig' ? 'selected' : ''}>${bioteigaT('plants_few', 'Wenig bepflanzt')}</option>
                <option value="viele" ${this.teichpflanzen === 'viele' ? 'selected' : ''}>${bioteigaT('plants_many', 'Stark bepflanzt')}</option>
              </select>
            </div>
          </div>

          <div class="teichwerte__pond-modal-actions">
            <button class="teichwerte__btn teichwerte__btn--outline" data-action="cancel-pond-edit">${bioteigaT('form_cancel', 'Abbrechen')}</button>
            <button class="teichwerte__btn teichwerte__btn--primary" data-action="save-pond">${bioteigaT('form_save', 'Speichern')}</button>
          </div>
        </div>
      </div>
    `;

    this.querySelector('.teichwerte').insertAdjacentHTML('beforeend', modalHtml);

    // Bind toggle buttons
    const toggleContainer = this.querySelector('.teichwerte__pond-toggle');
    if (toggleContainer) {
      toggleContainer.querySelectorAll('.teichwerte__pond-toggle-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          toggleContainer.querySelectorAll('.teichwerte__pond-toggle-btn').forEach(b => b.classList.remove('is-active'));
          btn.classList.add('is-active');
        });
      });
    }

    // Bind close/cancel
    this.querySelectorAll('[data-action="cancel-pond-edit"]').forEach(btn => {
      btn.addEventListener('click', () => this._hidePondEditModal());
    });

    // Bind overlay click to close
    const overlay = this.querySelector('[data-pond-overlay]');
    overlay?.addEventListener('click', (e) => {
      if (e.target === overlay) this._hidePondEditModal();
    });

    // Bind save
    this.querySelector('[data-action="save-pond"]')?.addEventListener('click', () => this._savePondData());
  }

  _hidePondEditModal() {
    this.querySelector('.teichwerte__pond-modal-overlay')?.remove();
  }

  async _savePondData() {
    const teichgroesse = parseInt(this.querySelector('[data-pond-field="teichgroesse"]')?.value, 10) || 0;
    const fischeActiveBtn = this.querySelector('.teichwerte__pond-toggle-btn.is-active');
    const fische = fischeActiveBtn?.dataset.value === 'true';
    const teichpflanzen = this.querySelector('[data-pond-field="teichpflanzen"]')?.value || 'keine';

    const saveBtn = this.querySelector('[data-action="save-pond"]');
    if (saveBtn) {
      saveBtn.disabled = true;
      saveBtn.textContent = bioteigaT('form_saving', 'Speichern...');
    }

    try {
      const tagsWorkerUrl = this.dataset.tagsWorkerUrl;
      if (!tagsWorkerUrl || !this.customerId) throw new Error('Worker URL oder Kunden-ID fehlt');

      const res = await fetch(tagsWorkerUrl + '/api/customer-metafields', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: this.customerId,
          teichgroesse,
          fische,
          teichpflanzen,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Speichern fehlgeschlagen');
      }

      const data = await res.json();

      // Update local state
      this.teichgroesse = data.metafields.teichgroesse;
      this.fische = data.metafields.fische;
      this.teichpflanzen = data.metafields.teichpflanzen;

      // Update data attributes
      this.dataset.teichgroesse = String(this.teichgroesse);
      this.dataset.fische = String(this.fische);
      this.dataset.teichpflanzen = this.teichpflanzen;

      this._hidePondEditModal();
      this._refreshPondOverview();
      this._showMessage(bioteigaT('msg_pond_saved', 'Teichdaten gespeichert!'), 'success');
    } catch (err) {
      console.error('Pond save error:', err);
      this._showMessage(bioteigaT('msg_error_save_pond', 'Fehler beim Speichern der Teichdaten.'), 'error');
    } finally {
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.textContent = bioteigaT('form_save', 'Speichern');
      }
    }
  }

  _refreshPondOverview() {
    const oldOverview = this.querySelector('.teichwerte__pond-overview');
    if (oldOverview) {
      const temp = document.createElement('div');
      temp.innerHTML = this._renderPondOverview();
      oldOverview.replaceWith(temp.firstElementChild);
      // Re-bind edit button
      this.querySelector('[data-action="edit-pond"]')
        ?.addEventListener('click', () => this._showPondEditModal());
      // Re-render health gauge
      this._renderHealthGauge();
    }
  }

  _showForm(reading = null) {
    this.view = 'form';
    this.querySelector('[data-view="dashboard"]')?.classList.add('teichwerte--hidden');
    this.querySelector('[data-view="form"]')?.classList.remove('teichwerte--hidden');
    if (reading) this._fillForm(reading);
  }

  _showDashboard() {
    this.view = 'dashboard';
    this.editingId = null;
    this._resetForm();
    this.querySelector('[data-view="dashboard"]')?.classList.remove('teichwerte--hidden');
    this.querySelector('[data-view="form"]')?.classList.add('teichwerte--hidden');
    this._renderDashboardContent();
  }

  // ---- API ----

  async _loadReadings() {
    try {
      const res = await fetch(this.proxyBase + '/readings');
      if (!res.ok) throw new Error('Fetch failed');
      const data = await res.json();
      this.readings = data.readings || [];
    } catch (err) {
      console.error('Failed to load readings:', err);
      this.readings = [];
    }
    this._sortReadings();
    this._renderDashboardContent();
  }

  _sortReadings() {
    this.readings.sort((a, b) => new Date(b.measured_at) - new Date(a.measured_at));
  }

  async _save() {
    const body = this._collectFormData();
    if (!body) return;

    const saveBtn = this.querySelector('[data-action="save"]');
    saveBtn.disabled = true;
    saveBtn.textContent = bioteigaT('form_saving', 'Speichern...');

    try {
      const url = this.editingId
        ? this.proxyBase + '/readings?reading_id=' + this.editingId
        : this.proxyBase + '/readings';
      const method = this.editingId ? 'PUT' : 'POST';

      // Kundeninfos mitsenden (für Supabase customers-Tabelle)
      body.customer_email = this.dataset.customerEmail || null;
      body.customer_first_name = this.dataset.customerFirstName || null;
      body.customer_last_name = this.dataset.customerLastName || null;

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error('Save failed');
      const data = await res.json();

      if (this.editingId) {
        const idx = this.readings.findIndex(r => r.id === this.editingId);
        if (idx >= 0) this.readings[idx] = data.reading;
      } else {
        this.readings.unshift(data.reading);
      }
      this._sortReadings();

      // Kunden-Tags für kritische/abweichende Werte aktualisieren (fire-and-forget)
      this._syncProblemTags(data.reading);

      this._showMessage(bioteigaT('msg_reading_saved', 'Messwerte gespeichert!'), 'success');
      this._showDashboard();
    } catch (err) {
      console.error('Save error:', err);
      this._showMessage(bioteigaT('msg_error_save', 'Fehler beim Speichern. Bitte versuchen Sie es erneut.'), 'error');
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = this.editingId ? bioteigaT('form_update', 'Aktualisieren') : bioteigaT('form_save', 'Speichern');
    }
  }

  async _deleteReading(id) {
    if (!confirm(bioteigaT('msg_confirm_delete', 'Messung wirklich löschen?'))) return;
    try {
      const res = await fetch(this.proxyBase + '/readings?reading_id=' + id, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      this.readings = this.readings.filter(r => r.id !== id);
      this._sortReadings();
      this._renderDashboardContent();
      this._showMessage(bioteigaT('msg_deleted', 'Messung gelöscht.'), 'success');
    } catch (err) {
      this._showMessage(bioteigaT('msg_error_delete', 'Fehler beim Löschen.'), 'error');
    }
  }

  // ---- Problem Tags Sync ----

  _syncProblemTags(reading) {
    const tagsWorkerUrl = this.dataset.tagsWorkerUrl;
    if (!tagsWorkerUrl || !this.customerId) return;

    // Sammle TW_-Tags für alle Werte mit Status warning oder bad
    const problemTags = [];
    for (const [key, problem] of Object.entries(WATER_PROBLEMS)) {
      if (reading[key] == null) continue;
      const status = this._getValueStatus(key, reading[key]);
      if (status !== 'good') {
        problemTags.push('TW_' + problem.tag);
      }
    }

    // Fire-and-forget: Fehler beim Tag-Update blockieren nie das Speichern
    fetch(tagsWorkerUrl + '/api/teichwerte-tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId: this.customerId, problemTags }),
    }).catch(err => console.warn('Tag-Sync fehlgeschlagen (unkritisch):', err));
  }

  // ---- Dashboard ----

  _renderDashboardContent() {
    this._renderHealthGauge();
    this._renderCards();
    this._renderRecommendations();
    this._renderHistory();
  }

  _renderRecommendations() {
    const container = this.querySelector('[data-recs-container]');
    if (!container) return;

    if (this.readings.length === 0) {
      container.innerHTML = '';
      return;
    }

    // Collect problems from the most recent reading per water value
    const problems = [];
    for (const [key, problem] of Object.entries(WATER_PROBLEMS)) {
      const def = WATER_VALUES[key];
      if (!def) continue;
      const withData = this.readings.filter(r => r[key] != null);
      if (withData.length === 0) continue;

      const current = withData[0][key];
      const status = this._getValueStatus(key, current);
      if (status === 'good') continue;

      // Determine high or low
      const isHigh = def.max !== null
        ? current > def.max
        : false; // unbounded max → can't be "too high" by our definition
      const isLow = current < def.min;

      const msg = isHigh && problem.high ? problem.high
                : isLow  && problem.low  ? problem.low
                : problem.high || problem.low;
      if (!msg) continue;

      problems.push({ key, def, status, msg, tag: problem.tag });
    }

    // Check for missing critical values in the latest reading
    const latestReading = this.readings[0] || {};
    const missingCritical = CRITICAL_UNMEASURED.filter(key => latestReading[key] == null);

    if (problems.length === 0 && missingCritical.length === 0) {
      container.innerHTML = '';
      return;
    }

    const collectionBase = '/collections/konfigurator';

    // Build missing-critical recommendation card
    let missingCard = '';
    if (missingCritical.length > 0) {
      const missingLabels = missingCritical.map(k => WATER_VALUES[k].symbol).join(', ');
      missingCard = `
        <div class="teichwerte__rec-card" style="background:#EFF6FF;border-color:#BFDBFE">
          <div class="teichwerte__rec-header">
            <span class="teichwerte__value-badge" style="background:#3B82F6">🔬</span>
            <span class="teichwerte__rec-title" style="color:#1E40AF">${bioteigaT('msg_more_values', '📋 Weitere Wasserwerte testen')}</span>
          </div>
          <p class="teichwerte__rec-desc">${bioteigaT('rec_more_desc', 'Wir empfehlen Ihnen unbedingt weitere Wasserwerte wie %{values} zu testen, denn diese Wasserwerte zeigen, ob die Algen in Ihrem Teich wachsen und das biologische Gleichgewicht intakt ist. Nur mit einem vollständigen Test können wir Ihnen eine zuverlässige Einschätzung Ihrer Teichgesundheit geben.', { values: missingLabels })}</p>
        </div>
      `;
    }

    const cards = problems.map(({ key, def, status, msg, tag }) => {
      const isWarning = status === 'warning';
      const bgColor  = isWarning ? '#FFFBEB' : '#FEF2F2';
      const bdColor  = isWarning ? '#FDE68A' : '#FECACA';
      const txColor  = isWarning ? '#92400E' : '#991B1B';
      const btnColor = isWarning ? '#D97706' : '#DC2626';
      const icon     = isWarning ? '⚠️' : '🚨';
      const url      = `${collectionBase}?filter.p.tag=${encodeURIComponent(tag)}`;

      return `
        <div class="teichwerte__rec-card" style="background:${bgColor};border-color:${bdColor}">
          <div class="teichwerte__rec-header">
            <span class="teichwerte__value-badge" style="background:${def.color}">${def.symbol}</span>
            <span class="teichwerte__rec-title" style="color:${txColor}">${icon} ${msg.title}</span>
          </div>
          <p class="teichwerte__rec-desc">${msg.desc}</p>
          <a href="${url}" class="teichwerte__rec-btn" style="background:${btnColor}">
            Jetzt beheben
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 8h10M9 4l4 4-4 4"/>
            </svg>
          </a>
        </div>
      `;
    }).join('');

    container.innerHTML = `
      <div class="teichwerte__recs-section" id="teichwerte-empfehlungen">
        <h3 class="teichwerte__recs-title">
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="10" cy="10" r="8"/>
            <path d="M10 6v4M10 14h.01"/>
          </svg>
          Empfehlungen
        </h3>
        <div class="teichwerte__recs-grid">${cards}${missingCard}</div>
      </div>
    `;
  }

  _renderCards() {
    const container = this.querySelector('[data-cards-container]');
    if (!container) return;

    const activeKeys = Object.keys(WATER_VALUES).filter(key =>
      this.readings.some(r => r[key] != null)
    );

    // Find unmeasured critical values (only if there are readings at all)
    const latestReading = this.readings[0] || null;
    const missingCriticalKeys = latestReading
      ? CRITICAL_UNMEASURED.filter(key => latestReading[key] == null)
      : [];

    if (activeKeys.length === 0 && missingCriticalKeys.length === 0) {
      container.innerHTML = '';
      return;
    }

    const cards = activeKeys.map(key => this._buildCard(key)).join('');
    const missingCards = missingCriticalKeys.map(key => this._buildMissingCard(key)).join('');
    container.innerHTML = `<div class="teichwerte__cards-grid">${cards}${missingCards}</div>`;
  }

  _buildCard(key) {
    const def = WATER_VALUES[key];
    const withData = this.readings.filter(r => r[key] != null);
    if (withData.length === 0) return '';

    const current = withData[0][key];
    const previous = withData[1]?.[key] ?? null;
    const status = this._getValueStatus(key, current);

    const statusColor = status === 'good' ? '#16A34A' : status === 'warning' ? '#D97706' : '#DC2626';
    const statusBg   = status === 'good' ? '#F0FDF4' : status === 'warning' ? '#FFFBEB' : '#FEF2F2';

    // Delta badge: always show status + arrow + diff
    const statusLabel = status === 'good' ? bioteigaT('status_optimal', 'Optimal') : status === 'warning' ? bioteigaT('status_deviation', 'Abweichung') : bioteigaT('status_critical', 'Kritisch');
    let deltaBadge = '';
    if (previous !== null) {
      const diff = current - previous;
      const decimals = def.step < 0.01 ? 3 : def.step < 0.1 ? 2 : 1;
      const diffStr = (diff >= 0 ? '+' : '') + diff.toFixed(decimals);
      const arrow = diff > 0 ? '↑' : diff < 0 ? '↓' : '→';
      deltaBadge = `
        <span class="teichwerte__delta-badge" style="color:${statusColor};background:${statusBg}">
          ${statusLabel} ${arrow} ${diffStr}
        </span>`;
    } else {
      deltaBadge = `
        <span class="teichwerte__delta-badge" style="color:${statusColor};background:${statusBg}">
          ${statusLabel}
        </span>`;
    }

    // Format current value
    const decimals = def.step < 0.01 ? 3 : def.step < 0.1 ? 2 : 1;
    const currentStr = parseFloat(current).toFixed(decimals).replace(/\.?0+$/, '') || current;

    // Optimal range label
    const optLabel = def.max !== null
      ? `${def.min}–${def.max}${def.unit ? ' ' + def.unit : ''}`
      : `≥ ${def.min}${def.unit ? ' ' + def.unit : ''}`;

    // Bar chart: last 3 on mobile, last 5 on desktop
    const isMobile = window.innerWidth < 600;
    const maxBars = isMobile ? 3 : 5;
    const chartReadings = [...withData].slice(0, maxBars).reverse();
    const chart = this._buildBarChart(key, def, chartReadings, statusColor, isMobile);

    return `
      <div class="teichwerte__card">
        <div class="teichwerte__card-top">
          <div class="teichwerte__card-identity">
            <span class="teichwerte__badge-lg" style="background:${def.color}">${def.symbol}</span>
            <span class="teichwerte__card-label">${def.label}</span>
          </div>
        </div>

        <div class="teichwerte__card-value-row">
          <span class="teichwerte__card-value">${currentStr}</span>
          ${def.unit ? `<span class="teichwerte__card-unit">${def.unit}</span>` : ''}
          ${deltaBadge}
        </div>

        <div class="teichwerte__card-chart">
          ${chart}
        </div>
      </div>
    `;
  }

  _buildMissingCard(key) {
    const def = WATER_VALUES[key];
    const optLabel = def.max !== null
      ? `${def.min}–${def.max}${def.unit ? ' ' + def.unit : ''}`
      : `≥ ${def.min}${def.unit ? ' ' + def.unit : ''}`;

    return `
      <div class="teichwerte__card teichwerte__card--missing">
        <div class="teichwerte__card-top">
          <div class="teichwerte__card-identity">
            <span class="teichwerte__badge-lg" style="background:${def.color}">${def.symbol}</span>
            <span class="teichwerte__card-label">${def.label}</span>
          </div>
        </div>

        <div class="teichwerte__card-value-row">
          <span class="teichwerte__card-value" style="color:#94A3B8">–</span>
          <span class="teichwerte__delta-badge" style="color:#1E40AF;background:#DBEAFE">
            📋 Unbedingt messen!
          </span>
        </div>

        <div class="teichwerte__card-missing-info">
          <p>${bioteigaT('optimal_range', 'Optimaler Bereich: %{range}', { range: optLabel })}</p>
        </div>
      </div>
    `;
  }

  _buildBarChart(key, def, readings, statusColor, isMobile = false) {
    const values = readings.map(r => r[key]);
    const dates  = readings.map(r => {
      const d = new Date(r.measured_at);
      return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}`;
    });

    // Mobile: narrow+tall viewBox → taller bars & larger text when scaled
    // Desktop: wide viewBox for 5 bars
    const W  = isMobile ? 280 : 500;
    const H  = isMobile ? 220 : 190;
    const pL = isMobile ? 12  : 14;
    const pR = isMobile ? 12  : 14;
    const pT = isMobile ? 32  : 28;
    const pB = isMobile ? 28  : 26;
    const fVal  = isMobile ? 16 : 15;   // font-size for value labels
    const fDate = isMobile ? 14 : 13;   // font-size for date labels
    const fOpt  = isMobile ? 13 : 12;   // font-size for optimal range labels
    const maxBarW = isMobile ? 72 : 60; // max bar width
    const cW = W - pL - pR;
    const cH = H - pT - pB;
    const baseY = pT + cH;

    const optMin = def.min;
    const optMax = def.max;

    // Tight Y range: focus on data + optimal markers
    const dataMin = Math.min(...values);
    const dataMax = Math.max(...values);

    let yLo = Math.min(dataMin, optMin);
    let yHi = optMax !== null
      ? Math.max(dataMax, optMax)
      : Math.max(dataMax, optMin + Math.max(dataMax - dataMin, optMin * 0.5, 2));

    if (yHi === yLo) { yHi = yLo + 1; }

    // Padding: tight — 12% of current span
    const rawSpan = yHi - yLo;
    const pad = rawSpan * 0.12;
    yLo = Math.max(0, yLo - pad);
    yHi = yHi + pad;
    const ySpan = yHi - yLo;

    const toY = v => pT + (1 - (v - yLo) / ySpan) * cH;

    const fmt = v => {
      if (Math.abs(v) >= 100) return Math.round(v).toString();
      if (Math.abs(v) >= 10)  return v.toFixed(1);
      if (Math.abs(v) >= 1)   return v.toFixed(1);
      if (Math.abs(v) >= 0.1) return v.toFixed(2);
      return v.toFixed(3);
    };

    // Optimal range
    const yOptLo = toY(optMin);
    const yOptHi = optMax !== null ? toY(optMax) : pT;
    const bandTop = Math.min(yOptLo, yOptHi);
    const bandH   = Math.abs(yOptLo - yOptHi);

    // Bars — wide, with value label above and date below
    const n = values.length;
    const slot = cW / n;
    const barW = Math.min(maxBarW, slot * 0.68);

    let bars = '', xlabels = '', vlabels = '';
    for (let i = 0; i < n; i++) {
      const v   = values[i];
      const st  = this._getValueStatus(key, v);
      const col = st === 'good' ? '#22C55E' : st === 'warning' ? '#F59E0B' : '#EF4444';
      const bx  = pL + i * slot + (slot - barW) / 2;
      const by  = toY(v);
      const bh  = Math.max(4, baseY - by);
      const cx  = bx + barW / 2;

      bars    += `<rect x="${bx.toFixed(1)}" y="${by.toFixed(1)}"
                       width="${barW.toFixed(1)}" height="${bh.toFixed(1)}"
                       rx="5" fill="${col}" fill-opacity="0.88"/>`;
      vlabels += `<text x="${cx.toFixed(1)}" y="${(by - 6).toFixed(1)}"
                       text-anchor="middle" font-size="${fVal}" font-weight="700"
                       fill="${col}" font-family="inherit">${fmt(v)}</text>`;
      xlabels += `<text x="${cx.toFixed(1)}" y="${H - 4}"
                       text-anchor="middle" font-size="${fDate}" fill="rgba(0,0,0,0.38)"
                       font-family="inherit">${dates[i]}</text>`;
    }

    // 3 subtle horizontal grid lines
    let grid = '';
    for (let i = 0; i <= 2; i++) {
      const gy = pT + (i / 2) * cH;
      grid += `<line x1="${pL}" y1="${gy.toFixed(1)}" x2="${W-pR}" y2="${gy.toFixed(1)}"
                    stroke="#EAEFF4" stroke-width="1"/>`;
    }

    // Optimal zone + lines with value labels
    const optAnnotation = `
      ${bandH > 1 ? `<rect x="${pL}" y="${bandTop.toFixed(1)}" width="${cW}"
           height="${bandH.toFixed(1)}" fill="${statusColor}" fill-opacity="0.07"/>` : ''}
      <line x1="${pL}" y1="${yOptLo.toFixed(1)}" x2="${W-pR}" y2="${yOptLo.toFixed(1)}"
            stroke="${statusColor}" stroke-width="1.5" stroke-dasharray="5,3" opacity="0.65"/>
      <text x="${(W - pR - 2).toFixed(1)}" y="${(yOptLo - 4).toFixed(1)}"
            text-anchor="end" font-size="${fOpt}" font-weight="700"
            fill="${statusColor}" opacity="0.8" font-family="inherit">${fmt(optMin)}</text>
      ${optMax !== null ? `
      <line x1="${pL}" y1="${yOptHi.toFixed(1)}" x2="${W-pR}" y2="${yOptHi.toFixed(1)}"
            stroke="${statusColor}" stroke-width="1.5" stroke-dasharray="5,3" opacity="0.65"/>
      <text x="${(W - pR - 2).toFixed(1)}" y="${(yOptHi - 4).toFixed(1)}"
            text-anchor="end" font-size="${fOpt}" font-weight="700"
            fill="${statusColor}" opacity="0.8" font-family="inherit">${fmt(optMax)}</text>` : ''}
    `;

    return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg"
                 style="width:100%;height:auto;display:block">
      ${grid}
      ${optAnnotation}
      ${bars}
      ${vlabels}
      ${xlabels}
      <line x1="${pL}" y1="${baseY}" x2="${W-pR}" y2="${baseY}"
            stroke="#D4DAE2" stroke-width="1"/>
    </svg>`;
  }

  _buildGauge(def, currentValue, statusColor) {
    const W = 200;
    const H = 26;
    const trackH = 8;
    const trackY = (H - trackH) / 2;
    const pX = 6;
    const trackW = W - pX * 2;

    const optMin = def.min;
    const optMax = def.max;

    // Display range
    let displayMin, displayMax;
    if (optMax !== null) {
      const range = optMax - optMin || optMin || 1;
      displayMin = Math.max(0, optMin - range * 0.35);
      displayMax = optMax + range * 0.35;
    } else {
      displayMin = 0;
      displayMax = Math.max(currentValue * 1.4, optMin * 2.2, optMin + 5);
    }

    const totalRange = displayMax - displayMin || 1;
    const toX = v => pX + Math.min(1, Math.max(0, (v - displayMin) / totalRange)) * trackW;

    const optMinX = toX(optMin);
    const optMaxX = optMax !== null ? toX(optMax) : pX + trackW;
    const optZoneW = Math.max(optMaxX - optMinX, 2);

    const cx = Math.max(pX + 5, Math.min(pX + trackW - 5, toX(currentValue)));
    const cy = H / 2;
    const r = trackH / 2 + 2;

    return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg"
                 style="width:100%;height:${H}px;display:block;overflow:visible">
      <rect x="${pX}" y="${trackY}" width="${trackW}" height="${trackH}"
            rx="${trackH / 2}" fill="#E8EDF2"/>
      <rect x="${optMinX.toFixed(1)}" y="${trackY}"
            width="${optZoneW.toFixed(1)}" height="${trackH}"
            fill="${statusColor}" fill-opacity="0.18"
            rx="0"/>
      <line x1="${optMinX.toFixed(1)}" y1="${trackY - 3}"
            x2="${optMinX.toFixed(1)}" y2="${trackY + trackH + 3}"
            stroke="${statusColor}" stroke-width="1.5" opacity="0.55"/>
      ${optMax !== null ? `<line x1="${optMaxX.toFixed(1)}" y1="${trackY - 3}"
            x2="${optMaxX.toFixed(1)}" y2="${trackY + trackH + 3}"
            stroke="${statusColor}" stroke-width="1.5" opacity="0.55"/>` : ''}
      <circle cx="${cx.toFixed(1)}" cy="${cy}" r="${r}"
              fill="white" stroke="${statusColor}" stroke-width="2"/>
      <circle cx="${cx.toFixed(1)}" cy="${cy}" r="${r - 3}"
              fill="${statusColor}"/>
    </svg>`;
  }

  _buildSparkline(key, def, sortedReadings, statusColor) {
    const values = sortedReadings.map(r => r[key]).filter(v => v != null);
    if (values.length === 0) return '';

    const W = 200;
    const H = 60;
    const pX = 4;
    const pY = 6;

    const optMin = def.min;
    const optMax = def.max; // null = unbounded

    // Y range: include data + optimal markers
    let yLow = Math.min(...values, optMin);
    let yHigh = optMax !== null ? Math.max(...values, optMax) : Math.max(...values, optMin * 1.3);
    if (yHigh === yLow) { yHigh += 1; yLow = Math.max(0, yLow - 1); }

    const pad = (yHigh - yLow) * 0.18;
    yLow = Math.max(0, yLow - pad);
    yHigh = yHigh + pad;
    const yRange = yHigh - yLow;

    const toY = v => pY + (1 - (v - yLow) / yRange) * (H - pY * 2);
    const toX = i => pX + (i / Math.max(values.length - 1, 1)) * (W - pX * 2);

    const yOptMin = toY(optMin);
    const yOptMax = optMax !== null ? toY(optMax) : pY;

    const bandTop = Math.min(yOptMin, yOptMax);
    const bandH = Math.abs(yOptMin - yOptMax);

    const pts = values.map((v, i) => `${toX(i).toFixed(1)},${toY(v).toFixed(1)}`).join(' ');
    const lx = toX(values.length - 1).toFixed(1);
    const ly = toY(values[values.length - 1]).toFixed(1);
    const fx = toX(0).toFixed(1);
    const baseY = (H - pY).toFixed(1);

    return `
      <svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg"
           style="width:100%;height:60px;display:block;overflow:visible">
        <!-- Optimal band -->
        ${optMax !== null && bandH > 0.5
          ? `<rect x="${pX}" y="${bandTop.toFixed(1)}" width="${W - pX * 2}" height="${bandH.toFixed(1)}"
                   fill="${statusColor}" fill-opacity="0.08" rx="2"/>`
          : ''}
        <!-- Min threshold -->
        <line x1="${pX}" y1="${yOptMin.toFixed(1)}" x2="${W - pX}" y2="${yOptMin.toFixed(1)}"
              stroke="${statusColor}" stroke-width="1.5" stroke-dasharray="4,3" opacity="0.55"/>
        <!-- Max threshold (if bounded) -->
        ${optMax !== null
          ? `<line x1="${pX}" y1="${yOptMax.toFixed(1)}" x2="${W - pX}" y2="${yOptMax.toFixed(1)}"
                   stroke="${statusColor}" stroke-width="1.5" stroke-dasharray="4,3" opacity="0.55"/>`
          : ''}
        <!-- Area fill -->
        <polygon points="${fx},${baseY} ${pts} ${lx},${baseY}"
                 fill="${def.color}" fill-opacity="0.09" stroke="none"/>
        <!-- Line -->
        <polyline points="${pts}" fill="none"
                  stroke="${def.color}" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round"/>
        <!-- Current dot -->
        <circle cx="${lx}" cy="${ly}" r="4" fill="${statusColor}" stroke="white" stroke-width="1.5"/>
      </svg>
    `;
  }

  // ---- History Table ----

  _renderHistory() {
    const container = this.querySelector('[data-history-container]');
    if (!container) return;

    if (this.readings.length === 0) {
      container.innerHTML = `
        <div class="teichwerte__history-empty">
          <div class="teichwerte__history-empty-icon">📋</div>
          <p>${bioteigaT('table_empty', 'Noch keine Messwerte vorhanden.')}</p>
          <p style="font-size:0.8125rem;margin-bottom:1.25rem;">
            ${bioteigaT('table_empty_hint', 'Tragen Sie Ihren ersten Test ein, um den Verlauf zu sehen.')}
          </p>
          <button class="teichwerte__btn teichwerte__btn--primary" data-action-empty="show-form">
            ${bioteigaT('add_values', '+ Werte hinzufügen')}
          </button>
        </div>
      `;
      container.querySelector('[data-action-empty="show-form"]')
        ?.addEventListener('click', () => this._showForm());
      return;
    }

    const activeKeys = Object.keys(WATER_VALUES)
      .filter(key => this.readings.some(r => r[key] != null));

    let headerHtml = `<th>${bioteigaT('table_date', 'Datum')}</th>`;
    for (const key of activeKeys) {
      headerHtml += `<th style="color:${WATER_VALUES[key].color}" title="${WATER_VALUES[key].label}">${WATER_VALUES[key].symbol}</th>`;
    }
    headerHtml += '<th></th>';

    let rowsHtml = '';
    for (const r of this.readings) {
      const date = new Date(r.measured_at).toLocaleDateString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      });
      let cells = `<td class="teichwerte__date-cell">${date}</td>`;
      for (const key of activeKeys) {
        const val = r[key];
        if (val == null) {
          cells += '<td class="teichwerte__cell--empty">—</td>';
        } else {
          const st = this._getValueStatus(key, val);
          cells += `<td class="teichwerte__cell--${st}">${val}</td>`;
        }
      }
      const noteBtn = r.notes ? `
            <button class="teichwerte__row-btn teichwerte__row-btn--note" data-note-id="${r.id}" title="${bioteigaT('action_show_note', 'Anmerkung anzeigen')}">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 1H2a1 1 0 0 0-1 1v12l3-2h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1Z"/>
                <path d="M5 5h6M5 8h4"/>
              </svg>
            </button>` : '';
      cells += `
        <td>
          <div class="teichwerte__row-actions">
            ${noteBtn}
            <button class="teichwerte__row-btn" data-edit-id="${r.id}" title="${bioteigaT('action_edit', 'Bearbeiten')}">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M11.5 2.5a1.414 1.414 0 0 1 2 2L5 13H3v-2L11.5 2.5Z"/>
              </svg>
            </button>
            <button class="teichwerte__row-btn teichwerte__row-btn--delete" data-delete-id="${r.id}" title="${bioteigaT('action_delete', 'Löschen')}">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M2 4h12M5 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1M6 7v5M10 7v5M3 4l1 9a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-9"/>
              </svg>
            </button>
          </div>
        </td>
      `;
      rowsHtml += `<tr>${cells}</tr>`;
    }

    container.innerHTML = `
      <div class="teichwerte__history-header">
        <h3 class="teichwerte__history-title">${bioteigaT('table_history', 'Verlauf')}</h3>
      </div>
      <div class="teichwerte__table-wrapper">
        <table class="teichwerte__table table--bordered">
          <thead><tr>${headerHtml}</tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </div>
    `;

    container.querySelectorAll('[data-edit-id]').forEach(btn => {
      btn.addEventListener('click', () => {
        const reading = this.readings.find(r => r.id === btn.dataset.editId);
        if (!reading) return;
        this.editingId = reading.id;
        this._showForm(reading);
        const title = this.querySelector('[data-form-title]');
        if (title) title.textContent = bioteigaT('form_title_edit', 'Werte bearbeiten');
        const saveBtn = this.querySelector('[data-action="save"]');
        if (saveBtn) saveBtn.textContent = 'Aktualisieren';
      });
    });
    container.querySelectorAll('[data-delete-id]').forEach(btn => {
      btn.addEventListener('click', () => this._deleteReading(btn.dataset.deleteId));
    });
    container.querySelectorAll('[data-note-id]').forEach(btn => {
      btn.addEventListener('click', () => {
        const reading = this.readings.find(r => r.id === btn.dataset.noteId);
        if (reading?.notes) this._showNoteModal(reading);
      });
    });
  }

  _showNoteModal(reading) {
    const date = new Date(reading.measured_at).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
    const overlay = document.createElement('div');
    overlay.className = 'teichwerte__note-modal-overlay';
    overlay.innerHTML = `
      <div class="teichwerte__note-modal">
        <div class="teichwerte__note-modal-header">
          <h3>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="18" height="18">
              <path d="M14 1H2a1 1 0 0 0-1 1v12l3-2h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1Z"/>
              <path d="M5 5h6M5 8h4"/>
            </svg>
            ${bioteigaT('note_from', 'Anmerkung vom %{date}', { date: date })}
          </h3>
          <button class="teichwerte__note-modal-close" data-close-note>&times;</button>
        </div>
        <div class="teichwerte__note-modal-body">
          <p>${reading.notes}</p>
        </div>
      </div>
    `;
    this.appendChild(overlay);
    overlay.querySelector('[data-close-note]').addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  }

  // ---- Form Helpers ----

  _collectFormData() {
    const isEdit = !!this.editingId;
    const dateInput = this.querySelector('[name="measured_at"]');
    const data = {
      measured_at: dateInput?.value || new Date().toISOString().split('T')[0],
    };

    let hasValue = false;
    let hasExtended = false;

    for (const [key, def] of Object.entries(WATER_VALUES)) {
      const input = this.querySelector(`[name="${key}"]`);
      if (input) {
        if (input.value !== '') {
          data[key] = parseFloat(input.value);
          hasValue = true;
          if (def.group === 'extended') hasExtended = true;
        } else if (isEdit) {
          data[key] = null; // Explicitly clear the value on update
        }
      }
    }

    if (!hasValue) {
      this._showMessage(bioteigaT('msg_need_one_value', 'Bitte geben Sie mindestens einen Messwert ein.'), 'error');
      return null;
    }

    data.test_type = hasExtended ? 'full' : 'simple';

    const notesInput = this.querySelector('[name="notes"]');
    if (notesInput && notesInput.value.trim()) {
      data.notes = notesInput.value.trim();
    } else if (isEdit) {
      data.notes = null;
    }

    return data;
  }

  _fillForm(reading) {
    const dateInput = this.querySelector('[name="measured_at"]');
    if (dateInput && reading.measured_at) dateInput.value = reading.measured_at;
    for (const key of Object.keys(WATER_VALUES)) {
      const input = this.querySelector(`[name="${key}"]`);
      if (input) input.value = reading[key] != null ? reading[key] : '';
    }
    const notesInput = this.querySelector('[name="notes"]');
    if (notesInput) notesInput.value = reading.notes || '';
  }

  _resetForm() {
    const dateInput = this.querySelector('[name="measured_at"]');
    if (dateInput) dateInput.value = new Date().toISOString().split('T')[0];
    this.querySelectorAll('[data-form] input[type="number"], [data-form-extended] input[type="number"]')
      .forEach(i => { i.value = ''; });
    const notesInput = this.querySelector('[name="notes"]');
    if (notesInput) notesInput.value = '';
    const title = this.querySelector('[data-form-title]');
    if (title) title.textContent = bioteigaT('form_title', 'Werte eintragen');
    const saveBtn = this.querySelector('[data-action="save"]');
    if (saveBtn) saveBtn.textContent = bioteigaT('form_save', 'Speichern');
  }

  _getValueStatus(key, value) {
    const def = WATER_VALUES[key];
    const inRange = value >= def.min && (def.max === null || value <= def.max);
    if (inRange) return 'good';
    const rangeFactor = def.max !== null ? (def.max - def.min) * 0.2 || 0.5 : def.min * 0.2 || 0.5;
    const nearRange = value >= def.min - rangeFactor && (def.max === null || value <= def.max + rangeFactor);
    return nearRange ? 'warning' : 'bad';
  }

  _showMessage(text, type) {
    const el = this.querySelector('[data-message]');
    if (!el) return;
    el.textContent = text;
    el.className = 'teichwerte__message is-' + type;
    setTimeout(() => { el.className = 'teichwerte__message'; }, 4000);
  }
}

if (!window.customElements.get('teichwerte-tracker')) {
  window.customElements.define('teichwerte-tracker', TeichwerteTracker);
}
